.root {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.main {
  width: min(100%, var(--max-w));
  margin: 0 auto;
  padding: clamp(20px, 3vw, 40px) clamp(16px, 3vw, 40px) 60px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.comparePrompt {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: var(--surface);
  border: 1px solid rgba(62, 122, 148, 0.30);
  border-radius: var(--radius-sm);
}

.compareLabel {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--steel);
  white-space: nowrap;
}

.compareInputShell {
  flex: 1;
  min-width: 140px;
  display: flex;
  align-items: center;
  height: 42px;
  padding: 0 14px;
  background: var(--canvas);
  border: 1px solid var(--border-md);
  border-radius: var(--radius-sm);
  transition: border-color 0.15s;
}

.compareInputShell:focus-within {
  border-color: var(--steel);
  box-shadow: 0 0 0 2px var(--steel);
}

.compareInput {
  flex: 1;
  /* iOS Safari auto-zooms the page on focus for any input under 16px —
     fixed px (not rem) so it stays 16px regardless of the body's 15px
     base font-size. */
  font-size: 16px;
  color: var(--text);
  min-width: 0;
  outline: none;
}

.compareInput::placeholder {
  color: var(--muted);
}

.compareSubmit {
  height: 42px;
  padding: 0 18px;
  background: var(--steel);
  color: #FFFFFF;
  font-size: 0.92rem;
  font-weight: 700;
  border-radius: var(--radius-sm);
  white-space: nowrap;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.compareSubmit:hover:not(:disabled) {
  opacity: 0.85;
}

.compareSubmit:disabled {
  opacity: 0.35;
}

.compareCancel {
  height: 42px;
  padding: 0 14px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--muted);
  border: 1px solid var(--border-md);
  border-radius: var(--radius-sm);
  transition: color 0.12s;
  flex-shrink: 0;
}

.compareCancel:hover {
  color: var(--soft);
}

.compareError {
  width: 100%;
  font-size: 0.88rem;
  color: var(--down);
  margin: 0;
}

.tabBar {
  display: flex;
  border-bottom: 1px solid var(--border);
  gap: 0;
  margin-bottom: 4px;
}

.tab {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px 11px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--muted);
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
}

/* Excluding the active tab: unqualified, this beat `.tabActive` on specificity
   and hovering the selected tab made it lighter, not darker. */
.tab:not(.tabActive):hover {
  color: var(--soft);
}

/* Same as the selected period segment: no hover, no pointer — clicking the tab
   you are already on does nothing. */
.tabActive {
  color: var(--text);
  border-bottom-color: var(--amber);
  cursor: default;
}

.tabCount {
  font-size: 0.8rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--soft);
  background: var(--tint-md);
  border-radius: 10px;
  padding: 1px 7px;
}

@keyframes tabFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.tabContent {
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: tabFadeIn 0.3s ease both;
}

/* Each section rises in on load so every card animates, not just the charts.
   Note: `animation-fill-mode: both` keeps a transform applied after the run —
   even the final `transform: none` computes to an identity matrix — so these
   rows stay a containing block for position:fixed descendants. Chart tooltips
   sidestep that by portalling to <body> (see ChartTooltip.jsx). */
@keyframes sectionIn {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: none; }
}

.tabContent > * {
  animation: sectionIn 0.45s ease both;
}

.tabContent > *:nth-child(2) { animation-delay: 70ms; }
.tabContent > *:nth-child(3) { animation-delay: 140ms; }
.tabContent > *:nth-child(4) { animation-delay: 210ms; }
.tabContent > *:nth-child(5) { animation-delay: 280ms; }

@media (prefers-reduced-motion: reduce) {
  .tabContent > * { animation: none; }
}

.workoutsWarning {
  padding: 10px 14px;
  font-size: 0.9rem;
  color: #7A4E0C;
  background: rgba(217, 126, 36, 0.12);
  border: 1px solid rgba(217, 126, 36, 0.35);
  border-radius: var(--radius-sm);
}

.trendsRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.exerciseRow {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

@media (max-width: 900px) {
  .trendsRow,
  .exerciseRow {
    grid-template-columns: minmax(0, 1fr);
  }
}
